import React, { useState, useRef, useEffect } from 'react';

import { useLoading, useFetchTWData, useFetchDasboardData, useSpreadsheets } from '../../context/stateContext';

import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import {
  useFetchTWDataCardInterface,
  useFetchTWDataUserInterface,
  useFetchTWDataInterface,
} from '../../types/stateContextTypes';
import { filterInterface } from '../../types/exportAllTypes';

const RANGE = {
  CURRENT: 'Current Month',
  PREVIOUS: 'Previous Month',
  BOTH: 'Previous And Current Month',
};

const SPREADSHEET_ID = '1kwpOfd4AajmawOjNZlVvHRZA39pqmmCNmtkC1OLqZ8c';
const TIME_OVERVIEW_SHEET_NAME = 'Overview';
const TIME_OVERVIEW_SHEET_COLUMNS = [
  'Date',
  'Project',
  'Who',
  'Company',
  'Task List',
  'Is it Billable?',
  'Decimal Hours',
  'Tags',
];
const USERS_SHEET_NAME = 'Teamwork users';
const USERS_SHEET_COLUMNS = ['ID', 'First name', 'Last name', 'Full name', 'Email'];
const VACATIONS_SHEET_NAME = 'Vacations';
const VACATIONS_SHEET_COLUMNS = ['Who', 'Start Date', 'End Date'];
const SICK_LEAVES_SHEET_NAME = 'Sick leaves';
const SICK_LEAVES_SHEET_COLUMNS = ['Who', 'Start Date', 'End Date'];

const getDateRange = (selectedRange: string) => {
  const date = new Date();
  let fromdate;
  let todate;
  if (selectedRange === RANGE.CURRENT) {
    fromdate = new Date(date.getFullYear(), date.getMonth(), 1);
    todate = date;
  } else if (selectedRange === RANGE.PREVIOUS) {
    fromdate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    todate = new Date(date.getFullYear(), date.getMonth(), 0);
  } else {
    fromdate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    todate = date;
  }
  return { fromdate, todate };
};

const transformDate = (date: Date) => {
  function pad(dateDigits: number) {
    return dateDigits.toString().padStart(2, '0');
  }
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
};

const transformDashboardDate = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
};

const ExportAll = () => {
  const [loading] = useLoading();
  const { load } = useFetchTWData();
  const { dashboardFetch } = useFetchDasboardData();
  const [updateTimeOverviewSheet, clearTimeOverviewSheet] = useSpreadsheets(SPREADSHEET_ID, TIME_OVERVIEW_SHEET_NAME);
  const [updateUsersSheet, clearUsersSheet] = useSpreadsheets(SPREADSHEET_ID, USERS_SHEET_NAME);
  const [updateVacationsSheet, clearVacationsSheet] = useSpreadsheets(SPREADSHEET_ID, VACATIONS_SHEET_NAME);
  const [updateSickLeavesSheet, clearSickLeavesSheet] = useSpreadsheets(SPREADSHEET_ID, SICK_LEAVES_SHEET_NAME);

  const [selectedRange, setSelectedRange] = useState(RANGE.CURRENT);
  const [dateRange, setDateRange] = useState<{ fromdate: Date; todate: Date } | null>(null);

  const [lastTimeEntry, setLastTimeEntry] = useState<string>(
    () => JSON.parse(window.localStorage.getItem('lastTimeEntry')!) || '',
  );
  const lastTimeEntryRef = useRef(lastTimeEntry);

  useEffect(() => {
    setDateRange(getDateRange(selectedRange));
  }, [selectedRange]);

  const onSelectedRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRange(event.target.value);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await updateTimeOverview();
    await updateVacations();
    await updateSickLeaves();
    await updateUsers();
  };

  const updateTimeOverview = async () => {
    try {
      if (dateRange === null) return;
      const range = {
        fromdate: transformDate(dateRange.fromdate),
        todate: transformDate(dateRange.todate),
      };
      let timeEntryData: useFetchTWDataCardInterface[] = await load(
        'time_entries.json',
        { ...range, pageSize: 500 },
        'time-entries',
      );
      let timeOverviewSpreadsheetData: (number | string | undefined)[][] =
        mapTimeOverviewToSpreadsheetData(timeEntryData);
      await clearTimeOverviewSheet();
      await updateTimeOverviewSheet(timeOverviewSpreadsheetData);
      saveLastTimeEntry();
    } catch (error) {}
  };

  const updateUsers = async () => {
    try {
      let userData = await load('people.json', { sort: 'lastName', pageSize: 500 }, 'people');
      let userSpreadsheetData: (string | number | undefined)[][] = mapUsersToSpreadsheetData(userData);
      await clearUsersSheet();
      await updateUsersSheet(userSpreadsheetData);
    } catch (error) {}
  };

  const updateVacations = async () => {
    try {
      if (dateRange === null) return;
      const allStatuses = await dashboardFetch('/holiday-statuses');
      const confirmId = allStatuses.find((status: { name: string }) => status.name === 'confirm');
      let filter = {
        user: 0,
        notes: '',
        year: dateRange.fromdate.getFullYear(),
        month: dateRange.fromdate.getMonth() + 1,
        status: [confirmId],
        time: ['future', 'in progress', 'expired'],
      };
      let data = await getVacations(filter);
      if (selectedRange === RANGE.BOTH) {
        filter = {
          ...filter,
          year: dateRange.todate.getFullYear(),
          month: dateRange.todate.getMonth() + 1,
        };
        const filteredVacations = await getVacations(filter);
        data = [...data, ...filteredVacations];
      }
      let vacationSpreadsheetData = data.map((vacation) => {
        const who =
          'firstName' in vacation && 'lastName' in vacation ? `${vacation.firstName} ${vacation.lastName}` : '';
        const from =
          'fromDate' in vacation && vacation.fromDate !== undefined ? transformDashboardDate(vacation.fromDate) : '';
        const to =
          'endDate' in vacation && vacation.endDate !== undefined ? transformDashboardDate(vacation.endDate) : '';
        return [who, from, to];
      });

      vacationSpreadsheetData.unshift(VACATIONS_SHEET_COLUMNS);
      await clearVacationsSheet();
      await updateVacationsSheet(vacationSpreadsheetData);
    } catch (error) {
      console.log(error);
    }
  };

  const getVacations = (filter: filterInterface) => {
    return dashboardFetch('/holiday-filter', null, {
      method: 'POST',
      body: JSON.stringify(filter),
    });
  };

  const updateSickLeaves = async () => {
    try {
      const allTypes = await dashboardFetch('/events/getAllTypes');
      const sickLeaveType = allTypes.find((type: { name: string }) => type.name === 'Sick leave');
      if (dateRange !== null) {
        const { fromdate, todate } = dateRange;
        const params = new URLSearchParams({
          idUser: '',
          startDate: (
            Date.UTC(fromdate.getFullYear(), fromdate.getMonth(), fromdate.getDate(), 0, 0, 0) / 1000
          ).toString(),
          endDate: (Date.UTC(todate.getFullYear(), todate.getMonth(), todate.getDate(), 23, 59, 59) / 1000).toString(),
        });
        if (sickLeaveType && 'types' in sickLeaveType) {
          for (const type of sickLeaveType.types!) {
            params.append('types[]', type.value.toString());
          }
        }
        let data = await dashboardFetch('/events/getAll', params);
        let sickLeaveSpreadsheetData = data.map((sickLeave) => {
          const who =
            'firstName' in sickLeave && 'lastName' in sickLeave ? `${sickLeave.firstName} ${sickLeave.lastName}` : '';
          const from =
            'dateFrom' in sickLeave && sickLeave.dateFrom !== undefined
              ? transformDashboardDate(sickLeave.dateFrom)
              : '';
          const to =
            'dateTo' in sickLeave && sickLeave.dateTo !== undefined ? transformDashboardDate(sickLeave.dateTo) : '';
          return [who, from, to];
        });
        sickLeaveSpreadsheetData.unshift(SICK_LEAVES_SHEET_COLUMNS);
        await clearSickLeavesSheet();
        await updateSickLeavesSheet(sickLeaveSpreadsheetData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const mapTimeOverviewToSpreadsheetData = (data: useFetchTWDataCardInterface[]): (number | string | undefined)[][] => {
    let latestTimeEntry = data[data.length - 1]['createdAt'];
    let updatedSpreadsheetData = data.reduceRight((acc: (number | string | undefined)[][], entry) => {
      if (entry['createdAt']! > latestTimeEntry!) {
        latestTimeEntry = entry['createdAt'];
      }
      acc.push([
        entry.date && new Date(entry.date).toLocaleDateString(),
        entry['project-name'],
        `${entry['person-last-name']} ${entry['person-first-name']}`,
        entry['company-name'],
        entry['todo-list-name'],
        entry.isbillable,
        entry.hoursDecimal,
        entry.tags!.map((tag: { name: string }) => tag.name).join(', '),
      ]);
      return acc;
    }, []);
    if (typeof latestTimeEntry === 'string') {
      lastTimeEntryRef.current = new Date(latestTimeEntry).toLocaleString();
    }
    updatedSpreadsheetData.unshift(TIME_OVERVIEW_SHEET_COLUMNS);
    return updatedSpreadsheetData;
  };

  const mapUsersToSpreadsheetData = (
    data: (useFetchTWDataInterface | useFetchTWDataCardInterface | useFetchTWDataUserInterface)[],
  ): (string | number | undefined)[][] => {
    let updatedUserSpreedsheetData: (string | number | undefined)[][] = data.map((entry) => {
      const firstName = 'first-name' in entry ? entry['first-name'] : undefined;
      const lastName = 'last-name' in entry ? entry['last-name'] : undefined;
      return [
        entry.id,
        firstName,
        lastName,
        `${firstName} ${lastName}`,
        'last-name' in entry ? entry['email-address'] : undefined,
      ];
    });
    updatedUserSpreedsheetData.unshift(USERS_SHEET_COLUMNS);
    return updatedUserSpreedsheetData;
  };

  const saveLastTimeEntry = () => {
    setLastTimeEntry(lastTimeEntryRef.current);
    window.localStorage.setItem('lastTimeEntry', JSON.stringify(lastTimeEntryRef.current));
  };

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '4rem' }}
    >
      <TextField
        id="time-period"
        select
        label="Range"
        value={selectedRange}
        onChange={onSelectedRangeChange}
        helperText={`Last time entry: ${lastTimeEntry}`}
        style={{ width: '100%', maxWidth: '240px' }}
      >
        <MenuItem value={RANGE.CURRENT}>{RANGE.CURRENT}</MenuItem>
        <MenuItem value={RANGE.BOTH}>{RANGE.BOTH}</MenuItem>
        <MenuItem value={RANGE.PREVIOUS}>{RANGE.PREVIOUS}</MenuItem>
      </TextField>
      <Button
        type="submit"
        color="primary"
        variant="contained"
        disabled={loading as boolean}
        style={{ marginLeft: '2rem' }}
      >
        Export
      </Button>
    </form>
  );
};

export default ExportAll;
